/**
 * Billing/Subscription API Service for Wadakaroo
 *
 * Handles all API calls for subscription and billing management:
 * - Subscription status and plan information
 * - Stripe Checkout session creation
 * - Billing portal access
 * - Plan changes and cancellation
 * - Invoice history
 */

import axiosInstance from './axiosInstance';

// ============================================
// Configuration Endpoints
// ============================================

/**
 * Get Stripe publishable key for frontend initialization
 * @returns {Promise<{publishableKey: string}>}
 */
export const getStripeConfigApi = async () => {
    const response = await axiosInstance.get('/billing/config');
    return response.data;
};

// ============================================
// Subscription Status Endpoints
// ============================================

/**
 * Get current subscription status for the organisation
 * @returns {Promise<{
 *   plan: string,
 *   status: string,
 *   stripeSubscriptionId: string,
 *   stripeCustomerId: string,
 *   currentPeriodStart: string,
 *   currentPeriodEnd: string,
 *   cancelAtPeriodEnd: boolean,
 *   canceledAt: string,
 *   amount: number,
 *   currency: string,
 *   interval: string,
 *   planDetails: Object
 * }>}
 */
export const getSubscriptionApi = async () => {
    const response = await axiosInstance.get('/billing/subscription');
    return response.data;
};

/**
 * Get available subscription plans
 * @returns {Promise<{
 *   plans: Array<{
 *     id: string,
 *     name: string,
 *     description: string,
 *     price: number,
 *     currency: string,
 *     interval: string,
 *     features: string[],
 *     limits: Object,
 *     isCurrent: boolean,
 *     isPopular: boolean
 *   }>,
 *   currentPlan: string
 * }>}
 */
export const getAvailablePlansApi = async () => {
    const response = await axiosInstance.get('/billing/plans');
    return response.data;
};

/**
 * Get current resource usage (used vs. plan limit) for the organisation.
 * Each resource is { used, limit, unlimited, atLimit }; limit -1 = unlimited.
 * @returns {Promise<{
 *   plan: string,
 *   organisationType: string,
 *   staff: { used: number, limit: number, unlimited: boolean, atLimit: boolean },
 *   vehicles: Object,
 *   workItemProfiles: Object,
 *   customers: Object
 * }>}
 */
export const getUsageApi = async () => {
    const response = await axiosInstance.get('/billing/usage');
    return response.data;
};

// ============================================
// Checkout Endpoints
// ============================================

/**
 * Create a Stripe Checkout session for subscribing to a plan
 * Returns a URL to redirect the user to Stripe's hosted checkout page
 * @param {string} plan - Plan ID (STARTER, PROFESSIONAL, ENTERPRISE)
 * @returns {Promise<{sessionId: string, url: string}>}
 */
export const createCheckoutSessionApi = async (plan) => {
    const response = await axiosInstance.post('/billing/checkout-session', { plan });
    return response.data;
};

/**
 * Create a Stripe Billing Portal session
 * Returns a URL to redirect the user to Stripe's customer portal
 * where they can manage payment methods, view invoices, etc.
 * @returns {Promise<{url: string}>}
 */
export const createBillingPortalSessionApi = async () => {
    const response = await axiosInstance.post('/billing/portal');
    return response.data;
};

// ============================================
// Plan Management Endpoints
// ============================================

/**
 * Change subscription plan (upgrade or downgrade)
 * @param {string} newPlan - New plan ID (STARTER, PROFESSIONAL, ENTERPRISE)
 * @returns {Promise<Object>} Updated subscription details
 */
export const changePlanApi = async (newPlan) => {
    const response = await axiosInstance.put('/billing/subscription/plan', { newPlan });
    return response.data;
};

/**
 * Cancel subscription
 * @param {boolean} immediate - If true, cancel immediately. If false, cancel at period end (default)
 * @returns {Promise<Object>} Updated subscription details
 */
export const cancelSubscriptionApi = async (immediate = false) => {
    const response = await axiosInstance.delete('/billing/subscription', {
        params: { immediate }
    });
    return response.data;
};

/**
 * Reactivate a subscription that was scheduled for cancellation
 * @returns {Promise<Object>} Updated subscription details
 */
export const reactivateSubscriptionApi = async () => {
    const response = await axiosInstance.post('/billing/subscription/reactivate');
    return response.data;
};

// ============================================
// Invoice Endpoints
// ============================================

/**
 * Get invoice history
 * @param {number} limit - Maximum number of invoices to return (default: 10)
 * @returns {Promise<{
 *   invoices: Array<{
 *     id: string,
 *     number: string,
 *     status: string,
 *     amount: number,
 *     currency: string,
 *     createdAt: string,
 *     paidAt: string,
 *     pdfUrl: string,
 *     hostedInvoiceUrl: string
 *   }>,
 *   hasMore: boolean
 * }>}
 */
export const getInvoicesApi = async (limit = 10) => {
    const response = await axiosInstance.get('/billing/invoices', {
        params: { limit }
    });
    return response.data;
};

// ============================================
// Helper Functions
// ============================================

/**
 * Format currency amount for display
 * @param {number} amount - Amount in dollars
 * @param {string} currency - Currency code (e.g., 'AUD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'AUD') => {
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency,
    }).format(amount);
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

/**
 * Get status badge color class
 * @param {string} status - Subscription status
 * @returns {string} Tailwind CSS classes for the badge
 */
export const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
        case 'ACTIVE':
            return 'bg-green-100 text-green-800';
        case 'TRIALING':
            return 'bg-blue-100 text-blue-800';
        case 'PAST_DUE':
            return 'bg-yellow-100 text-yellow-800';
        case 'CANCELED':
            return 'bg-gray-100 text-gray-800';
        case 'INCOMPLETE':
        case 'INCOMPLETE_EXPIRED':
            return 'bg-orange-100 text-orange-800';
        case 'UNPAID':
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-gray-100 text-gray-600';
    }
};

/**
 * Check if subscription status allows access to paid features
 * @param {string} status - Subscription status
 * @returns {boolean}
 */
export const hasActiveSubscription = (status) => {
    return ['ACTIVE', 'TRIALING'].includes(status?.toUpperCase());
};

/**
 * Check if subscription status requires user action
 * @param {string} status - Subscription status
 * @returns {boolean}
 */
export const requiresAction = (status) => {
    return ['PAST_DUE', 'INCOMPLETE', 'UNPAID'].includes(status?.toUpperCase());
};

/**
 * Detect the backend's "plan limit reached" response (HTTP 402,
 * error code PLAN_LIMIT_EXCEEDED) — thrown when an action exceeds the org's
 * subscription plan limit (e.g. adding staff beyond the plan's max).
 * @param {Error} error - an axios error
 * @returns {boolean}
 */
export const isPlanLimitError = (error) => {
    return error?.response?.status === 402
        && error?.response?.data?.error === 'PLAN_LIMIT_EXCEEDED';
};

/**
 * Extract a user-facing message from an API error, falling back to a generic one.
 * For plan-limit errors this is the "Upgrade your plan…" message from the backend.
 * @param {Error} error - an axios error
 * @param {string} [fallback] - message to use when the response has none
 * @returns {string}
 */
export const getApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
    return error?.response?.data?.message || error?.message || fallback;
};

// ============================================
// Default Export
// ============================================

const billingApi = {
    // Configuration
    getStripeConfigApi,
    // Subscription
    getSubscriptionApi,
    getAvailablePlansApi,
    getUsageApi,
    // Checkout
    createCheckoutSessionApi,
    createBillingPortalSessionApi,
    // Plan Management
    changePlanApi,
    cancelSubscriptionApi,
    reactivateSubscriptionApi,
    // Invoices
    getInvoicesApi,
    // Helpers
    formatCurrency,
    formatDate,
    getStatusBadgeClass,
    hasActiveSubscription,
    requiresAction,
    isPlanLimitError,
    getApiErrorMessage,
};

export default billingApi;
